import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import { fetchJson } from '../../lib/apiFetchJson';
import { FlowLookupResponseSchema, type FlowLookupResponse } from '@shared/flow/identifier';

/**
 * Trigger 2 (plan D12/U5) — precise identifier lookup. Fired instead of
 * `useFlowSearch` when the debounced query is identifier-shaped per
 * `parseSearchIdentifier` (imported from the shared module so detection here
 * can never drift from the backend's own classification). Mode-independent:
 * this hook and its render branch don't consult `contactsOnly` at all — the
 * D8 mode gate only ever applied to FUZZY account results.
 *
 * NO `keepPreviousData` (bug fix — unlike `useFlowSearch` in GlobalSearch.tsx):
 * a one-row precise lookup has no "flash to empty mid-typing" concern to
 * guard against, and keeping the previous identifier's result around while a
 * NEW identifier's query is in flight is actively dangerous here. React
 * Query's `placeholderData` makes `data`/`isLoading` report the OLD query's
 * result as already-settled ("stale but a hasData"-non-'pending'  state) even
 * though a DIFFERENT `queryKey` (the new debounced `q`) is now fetching — so
 * typing identifier A, letting it resolve, then replacing it with identifier
 * B and hitting Enter before B resolves used to select A: Enter's
 * `!hasNavigated` branch (in GlobalSearch.tsx) reads `items[0]` off whatever
 * `lookup.data` currently is, and with `keepPreviousData` that was still A's
 * row with `isLoading: false`. Without it, `data`/`items` are correctly empty
 * and `isLoading` is correctly true until B's OWN fetch (keyed on B)
 * resolves.
 *
 * Extracted out of GlobalSearch.tsx (was a private hook there) so the short
 * entity-entry routes' `ContactRedirect`
 * (`web/app-home/src/components/flow/entity-routes.tsx`) can reuse the exact
 * same lookup without importing GlobalSearch's whole render tree.
 */
export function useFlowLookup(q: string, enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery<FlowLookupResponse>({
    queryKey: ['flow-lookup', q],
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const qs = new URLSearchParams({ q });
      return fetchJson(apiFetch, FlowLookupResponseSchema, `/api/flow/lookup?${qs.toString()}`, { timeoutMs: 30_000 });
    },
  });
}
